/**
 * Step colour for a two-line band (HTF Band chip and Gandiv's band). Owner's rule,
 * 2026-09-25:
 *
 *   BOTH lines stepped UP vs the previous step   -> rising colour (fill and lines)
 *   BOTH lines stepped DOWN vs the previous step -> falling colour
 *   anything else (one up / one down, or a line exactly flat, or no previous step)
 *                                                 -> the regression direction, as before
 *
 * Before this, the colour was ONLY the regression direction (lrClose >= lrOpen), so a
 * band whose two lines were both climbing could still paint red, which reads wrong.
 *
 * What "the previous step" is differs by band and is the caller's choice:
 *   - HTF Band: the previous HIGHER-timeframe bar (its value is flat across its window);
 *   - Gandiv:   the previous CHART bar (its as-known band can move on every bar).
 */
export interface BandStep { upper: number; lower: number }

export function bandStepUp(prev: BandStep | null | undefined, cur: BandStep, regressionUp: boolean): boolean {
  if (prev && Number.isFinite(prev.upper) && Number.isFinite(prev.lower)) {
    if (cur.upper > prev.upper && cur.lower > prev.lower) return true;
    if (cur.upper < prev.upper && cur.lower < prev.lower) return false;
  }
  return regressionUp;
}

/**
 * The HTF Band's stamps are one per CHART bar, repeating the covering HTF bar's body
 * across its window. The step rule must compare HTF bar with HTF bar, so this walks
 * the stamps, notices where the covering HTF body changes, and colours each stamp by
 * its HTF bar vs the one before it. Joined on nothing but order: stamps are ascending.
 *
 * A new HTF bar is recognised by a change of body. Two consecutive HTF bars with
 * bit-identical bodies are therefore one step, which is exactly the "flat" case the
 * rule leaves to the regression direction anyway.
 */
export function colourHtfStamps<T extends BandStep & { up: boolean }>(stamps: T[]): boolean[] {
  const out: boolean[] = new Array(stamps.length);
  let prevBar: BandStep | null = null;
  let curBar: BandStep | null = null;
  for (let i = 0; i < stamps.length; i++) {
    const s = stamps[i];
    if (!curBar || s.upper !== curBar.upper || s.lower !== curBar.lower) {
      prevBar = curBar;
      curBar = { upper: s.upper, lower: s.lower };
    }
    out[i] = bandStepUp(prevBar, curBar, s.up);
  }
  return out;
}
